/**
 * Deslizador de dues nanses per al filtre de preu.
 *
 * No filtra res pel seu compte: escriu als dos camps de text que Horizon ja
 * té (ref="minInput" i ref="maxInput") i hi dispara un `change`. Aquest event
 * puja fins a <price-facet-component>, que és qui crida
 * /updatePriceFilterAndResults i recarrega la graella. Així la validació, la
 * URL i el resum del filtre segueixen sent els del tema.
 *
 * Va amb enters (euros sencers). Els camps de Horizon porten el preu amb el
 * format local —«25,00»—, i Number() no sap llegir la coma: escrivint-hi
 * enters ens estalviem la conversió i el filtre segueix funcionant igual.
 *
 * És un element a mida, no un script solt, perquè en filtrar Horizon torna a
 * pintar la secció sencera amb innerHTML i els <script> no s'hi tornen a
 * executar; els elements a mida, en canvi, es reactiven sols.
 */
class LacPriceRange extends HTMLElement {
  connectedCallback() {
    this.minRange = this.querySelector('.lac-range__in--min');
    this.maxRange = this.querySelector('.lac-range__in--max');

    const facet = this.closest('price-facet-component');
    this.minField = facet?.querySelector('[ref="minInput"]');
    this.maxField = facet?.querySelector('[ref="maxInput"]');

    if (!this.minRange || !this.maxRange || !this.minField || !this.maxField) return;

    this.addEventListener('input', this.#onSlide);
    this.addEventListener('change', this.#onCommit);
    this.minField.addEventListener('change', this.#onFieldChange);
    this.maxField.addEventListener('change', this.#onFieldChange);

    this.#paint();
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.#onSlide);
    this.removeEventListener('change', this.#onCommit);
    this.minField?.removeEventListener('change', this.#onFieldChange);
    this.maxField?.removeEventListener('change', this.#onFieldChange);
  }

  get #top() {
    return Number(this.maxRange.max) || 0;
  }

  /** Les dues nanses no es poden creuar. */
  #clamp() {
    const min = Number(this.minRange.value);
    const max = Number(this.maxRange.value);
    if (min > max) {
      if (document.activeElement === this.minRange) this.maxRange.value = String(min);
      else this.minRange.value = String(max);
    }
  }

  /** Pinta el tram seleccionat entre les dues nanses. */
  #paint() {
    const top = this.#top;
    if (!top) return;
    this.style.setProperty('--lac-range-a', `${(Number(this.minRange.value) / top) * 100}%`);
    this.style.setProperty('--lac-range-b', `${(Number(this.maxRange.value) / top) * 100}%`);
  }

  /* Mentre s'arrossega: només actualitzem el dibuix i els camps, sense
     recarregar la graella a cada píxel. */
  #onSlide = (event) => {
    if (event.target !== this.minRange && event.target !== this.maxRange) return;

    this.#clamp();
    this.#paint();

    const min = Number(this.minRange.value);
    const max = Number(this.maxRange.value);

    // Un extrem al seu lloc de sempre vol dir «sense límit»: deixant el camp
    // buit, el filtre no surt ni a la URL ni al resum.
    this.minField.value = min > 0 ? String(min) : '';
    this.maxField.value = max < this.#top ? String(max) : '';
  };

  /* En deixar anar la nansa sí que filtrem. */
  #onCommit = (event) => {
    if (event.target !== this.minRange && event.target !== this.maxRange) return;

    event.stopPropagation();
    this.maxField.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /* I si algú escriu als camps a mà, les nanses el segueixen. */
  #onFieldChange = () => {
    const top = this.#top;
    const min = Number(this.minField.value.replace(',', '.')) || 0;
    const max = Number(this.maxField.value.replace(',', '.')) || top;

    this.minRange.value = String(Math.max(0, Math.min(Math.round(min), top)));
    this.maxRange.value = String(Math.max(0, Math.min(Math.round(max), top)));
    this.#paint();
  };
}

if (!customElements.get('lac-price-range')) {
  customElements.define('lac-price-range', LacPriceRange);
}
